import * as React from 'react';
import { FileSpreadsheet, Link2, PencilLine } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FilePicker,
  Stepper,
  Textarea,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../../lib/requestErrorToast';
import { useBusyDialogGuard } from '../../../../../../hooks/useBusyDialogGuard';
import { dashboardT } from '../../../../../../lib/messages';
import { downloadCsv } from '../../../../../../lib/downloadBlob';
import { useSupplyAppsMutations, useSupplyAppsReferences } from '../../../hooks';
import {
  countPublisherAppImportStatuses,
  createPublisherAppDraft,
  serializePublisherAppsForCreate,
  serializePublisherAppsForValidation,
  splitPublisherAppStoreUrls,
} from '../../../publisherAppsImport';
import {
  PublisherAppImportStatus,
  PublisherAppsAddMethod,
  PublisherAppsAddStep,
  type PublisherAppImportDraft,
} from '../../../model';
import { PublisherAppsEditorTable } from '../PublisherAppsEditorTable';
import { PublisherAppsMethodCard } from '../PublisherAppsMethodCard';

const steps = [
  { id: PublisherAppsAddStep.Method, label: 'Choose a method' },
  { id: PublisherAppsAddStep.Input, label: 'Adding apps' },
  { id: PublisherAppsAddStep.Preview, label: 'Preview' },
];

export function PublisherAddAppsDialog({
  publisherId,
  onClose,
}: {
  publisherId: string;
  onClose: () => void;
}) {
  const [step, setStep] = React.useState(PublisherAppsAddStep.Method);
  const [method, setMethod] = React.useState<PublisherAppsAddMethod | null>(null);
  const [urls, setUrls] = React.useState('');
  const [file, setFile] = React.useState<File | null>(null);
  const [rows, setRows] = React.useState<PublisherAppImportDraft[]>([createPublisherAppDraft()]);
  const [previewRows, setPreviewRows] = React.useState<PublisherAppImportDraft[]>([]);
  const mutations = useSupplyAppsMutations();
  const references = useSupplyAppsReferences({ publishersEnabled: false });
  const pending =
    mutations.previewPublisherAppsFromUrls.isPending ||
    mutations.previewPublisherAppsManually.isPending ||
    mutations.previewPublisherAppsFromCsv.isPending ||
    mutations.createPublisherApps.isPending;
  const guard = useBusyDialogGuard(pending, onClose);
  const parsedUrls = splitPublisherAppStoreUrls(urls);
  const inputValid = isPublisherAppsInputValid(method, parsedUrls, file, rows);
  const counts = countPublisherAppImportStatuses(previewRows);
  const previewValid =
    previewRows.length > 0 &&
    counts[PublisherAppImportStatus.Invalid] === 0 &&
    counts[PublisherAppImportStatus.Duplicate] === 0;

  function selectMethod(nextMethod: PublisherAppsAddMethod) {
    setMethod(nextMethod);
    setPreviewRows([]);
    setStep(PublisherAppsAddStep.Input);
  }

  async function preview() {
    if (!method || !inputValid) return;
    try {
      let response;
      if (method === PublisherAppsAddMethod.StoreUrls) {
        response = await mutations.previewPublisherAppsFromUrls.mutateAsync({
          publisherId,
          urls: parsedUrls,
        });
      } else if (method === PublisherAppsAddMethod.Csv && file) {
        response = await mutations.previewPublisherAppsFromCsv.mutateAsync({ publisherId, file });
      } else {
        response = await mutations.previewPublisherAppsManually.mutateAsync({
          publisherId,
          apps: serializePublisherAppsForValidation(rows),
        });
      }
      setPreviewRows(response.data);
      setStep(PublisherAppsAddStep.Preview);
    } catch (error) {
      showRequestError(error, dashboardT('publisherDetails.apps.previewError'));
    }
  }

  async function save() {
    if (!previewValid) return;
    try {
      const response = await mutations.createPublisherApps.mutateAsync({
        publisherId,
        apps: serializePublisherAppsForCreate(publisherId, previewRows),
      });
      if (response.data.length === 0 || response.data.some((app) => app.uniqueApp === false)) {
        toast.error(dashboardT('publisherDetails.apps.noAppsCreated'));
        return;
      }
      toast.success(dashboardT('publisherDetails.apps.addSuccess'));
      onClose();
    } catch (error) {
      showRequestError(error, dashboardT('publisherDetails.apps.addError'));
    }
  }

  async function downloadTemplate() {
    try {
      downloadCsv(
        await mutations.downloadPublisherAppsCsvTemplate.mutateAsync(publisherId),
        'apps-template.csv',
      );
    } catch (error) {
      showRequestError(error, dashboardT('publisherDetails.apps.templateError'));
    }
  }

  return (
    <Dialog open onOpenChange={guard.onOpenChange}>
      <DialogContent
        className="flex max-h-[92vh] max-w-6xl flex-col gap-0 overflow-hidden p-0"
        onEscapeKeyDown={guard.onDismissInteraction}
        onPointerDownOutside={guard.onDismissInteraction}
      >
        <DialogHeader className="border-border border-b px-5 py-4">
          <DialogTitle>{dashboardT('publisherDetails.apps.addTitle')}</DialogTitle>
          <DialogDescription>
            {dashboardT('publisherDetails.apps.addDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className="border-border border-b px-5 py-4">
          <Stepper
            steps={steps}
            currentId={step}
            completedIds={publisherAppsCompletedSteps(step)}
            onStepClick={(id) => {
              const selectedStep = id as PublisherAppsAddStep;
              if (!pending && selectedStep !== PublisherAppsAddStep.Preview) {
                setStep(selectedStep);
                setPreviewRows([]);
              }
            }}
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {step === PublisherAppsAddStep.Method ? (
            <div className="grid gap-4 md:grid-cols-3">
              <PublisherAppsMethodCard
                icon={<Link2 />}
                label={dashboardT('publisherDetails.apps.addByUrl')}
                onClick={() => {
                  selectMethod(PublisherAppsAddMethod.StoreUrls);
                }}
              />
              <PublisherAppsMethodCard
                icon={<PencilLine />}
                label={dashboardT('publisherDetails.apps.addManually')}
                onClick={() => {
                  selectMethod(PublisherAppsAddMethod.Manual);
                }}
              />
              <PublisherAppsMethodCard
                icon={<FileSpreadsheet />}
                label={dashboardT('publisherDetails.apps.addByCsv')}
                onClick={() => {
                  selectMethod(PublisherAppsAddMethod.Csv);
                }}
              />
            </div>
          ) : null}
          {step === PublisherAppsAddStep.Input ? (
            <div className="space-y-5">
              {method === PublisherAppsAddMethod.StoreUrls ? (
                <Textarea
                  aria-label={dashboardT('publisherDetails.apps.storeUrls')}
                  placeholder={dashboardT('publisherDetails.apps.storeUrlsPlaceholder')}
                  value={urls}
                  disabled={pending}
                  rows={8}
                  onChange={(event) => {
                    setUrls(event.target.value);
                  }}
                />
              ) : null}
              {method === PublisherAppsAddMethod.Manual ? (
                <PublisherAppsEditorTable
                  rows={rows}
                  onRowsChange={setRows}
                  platforms={references.platforms.data ?? []}
                  iabCategories={references.iabCategories.data ?? []}
                  disabled={pending}
                  preview={false}
                />
              ) : null}
              {method === PublisherAppsAddMethod.Csv ? (
                <div className="space-y-4">
                  <FilePicker
                    label={dashboardT('publisherDetails.csvFile')}
                    buttonLabel={dashboardT('publisherDetails.chooseFile')}
                    accept=".csv,text/csv"
                    value={file}
                    onFileChange={setFile}
                    required
                    disabled={pending}
                  />
                  <p className="text-text-secondary text-sm">
                    {dashboardT('publisherDetails.apps.csvRequired')}
                  </p>
                  <Button type="button" variant="outline" onClick={() => void downloadTemplate()}>
                    {dashboardT('publisherDetails.apps.downloadTemplate')}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
          {step === PublisherAppsAddStep.Preview ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-text-secondary text-sm">Apps in list: {previewRows.length}</p>
                <div className="flex gap-4 text-sm">
                  <span className="text-success">Valid: {counts.VALID}</span>
                  <span>Duplicated: {counts.DUPLICATE}</span>
                  <span className="text-destructive">Invalid: {counts.INVALID}</span>
                </div>
              </div>
              <PublisherAppsEditorTable
                rows={previewRows}
                onRowsChange={setPreviewRows}
                platforms={references.platforms.data ?? []}
                iabCategories={references.iabCategories.data ?? []}
                disabled={pending}
                preview
              />
              {!previewValid ? (
                <p className="text-destructive text-sm">
                  {dashboardT('publisherDetails.apps.previewInvalid')}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
        <DialogFooter className="border-border border-t px-5 py-4">
          <Button type="button" variant="outline" disabled={pending} onClick={guard.close}>
            {dashboardT('publisherDetails.cancel')}
          </Button>
          {step === PublisherAppsAddStep.Input ? (
            <Button type="button" disabled={!inputValid || pending} onClick={() => void preview()}>
              {dashboardT('publisherDetails.apps.preview')}
            </Button>
          ) : null}
          {step === PublisherAppsAddStep.Preview ? (
            <Button type="button" disabled={!previewValid || pending} onClick={() => void save()}>
              {dashboardT('publisherDetails.save')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function isPublisherAppsInputValid(
  method: PublisherAppsAddMethod | null,
  parsedUrls: string[],
  file: File | null,
  rows: PublisherAppImportDraft[],
) {
  if (method === PublisherAppsAddMethod.StoreUrls) return parsedUrls.length > 0;
  if (method === PublisherAppsAddMethod.Csv) return Boolean(file);
  return (
    method === PublisherAppsAddMethod.Manual &&
    rows.length > 0 &&
    rows.every((row) => Boolean(row.appStoreId?.trim() && row.platform?.id))
  );
}

function publisherAppsCompletedSteps(step: PublisherAppsAddStep) {
  const completed = new Set<string>();
  if (step !== PublisherAppsAddStep.Method) completed.add(PublisherAppsAddStep.Method);
  if (step === PublisherAppsAddStep.Preview) completed.add(PublisherAppsAddStep.Input);
  return completed;
}
